import { queryOptions } from '@tanstack/react-query'
import type { Database } from '@/types/supabase'
import { countItemStats } from '@/lib/bulk-jobs/status'
import type { BulkJobStatus } from '@/lib/bulk-jobs/types'
import { supabase } from '@/lib/supabase/client'

// Re-export shared status type for backwards compatibility
export type { BulkJobStatus as VtoBulkJobStatus }

// Re-export shared deriveJobStatus as deriveVtoJobStatus for backwards compatibility
export { deriveJobStatus as deriveVtoJobStatus } from '@/lib/bulk-jobs/status'

// =============================================================================
// VTO Bulk Job Types
// =============================================================================

type VtoBulkJobRow = Database['public']['Tables']['vto_bulk_jobs']['Row']

/**
 * VTO Bulk job with author profile and progress counts.
 * Status is derived from items, not the DB status column.
 */
export type VtoBulkJob = VtoBulkJobRow & {
  profiles: {
    id: string
    email: string
    full_name: string | null
    avatar_url: string | null
  } | null
  /** Number of items that are done (completed or failed) - derived from items */
  processed_items: number
  /** Number of items that failed or errored */
  failed_items: number
}

/**
 * Active bulk job with progress information for the indicator
 */
export type VtoBulkJobWithProgress = {
  id: string
  name: string
  status: BulkJobStatus
  total_items: number
  created_at: string
  /** Number of items that are done (completed or failed) */
  processed_items: number
  /** Progress percentage (0-100) */
  progress_percent: number
}

// =============================================================================
// Helpers
// =============================================================================

type VtoBulkJobItemRaw = {
  error_message: string | null
  vto_look_id: string | null
  vto_looks: {
    generations: {
      status: string
    } | null
  } | null
}

function toStatusItem(item: VtoBulkJobItemRaw) {
  return {
    error_message: item.error_message,
    result_entity_id: item.vto_look_id,
    generation_status: item.vto_looks?.generations?.status ?? null,
  }
}

// =============================================================================
// Raw fetch functions (used by unified bulk-jobs query)
// =============================================================================

export async function fetchVtoAllJobs(
  workspaceId: string
): Promise<VtoBulkJob[]> {
  if (!workspaceId || workspaceId.trim() === '') {
    throw new Error(
      'Invalid workspaceId: cannot fetch VTO bulk jobs without valid workspace ID'
    )
  }

  const { data, error } = await supabase
    .from('vto_bulk_jobs')
    .select(
      `
      *,
      profiles!author(id, email, full_name, avatar_url),
      vto_bulk_job_items!bulk_job_id(
        error_message,
        vto_look_id,
        vto_looks!vto_look_id(
          generations!current_generation_id(status)
        )
      )
    `
    )
    .eq('workspace_id', workspaceId)
    .order('created_at', { ascending: false })

  if (error) {
    throw new Error(`Failed to fetch VTO bulk jobs: ${error.message}`, {
      cause: error,
    })
  }

  return (data || []).map((job) => {
    const stats = countItemStats(job.vto_bulk_job_items || [], toStatusItem)
    return {
      ...job,
      processed_items: stats.processed,
      failed_items: stats.failed,
      vto_bulk_job_items: undefined,
    }
  }) as unknown as VtoBulkJob[]
}

// =============================================================================
// VTO Bulk Job Queries
// =============================================================================

export const vtoBulkJob = {
  findOne: (jobId: string) =>
    queryOptions({
      queryKey: ['vto-bulk-job', jobId],
      queryFn: async (): Promise<VtoBulkJob | null> => {
        if (!jobId || jobId.trim() === '') {
          return null
        }

        const { data, error } = await supabase
          .from('vto_bulk_jobs')
          .select(
            `
            *,
            profiles!author(id, email, full_name, avatar_url),
            vto_bulk_job_items!bulk_job_id(
              error_message,
              vto_look_id,
              vto_looks!vto_look_id(
                generations!current_generation_id(status)
              )
            )
          `
          )
          .eq('id', jobId)
          .maybeSingle()

        if (error) {
          throw new Error(`Failed to fetch VTO bulk job: ${error.message}`, {
            cause: error,
          })
        }

        if (!data) {
          return null
        }

        const stats = countItemStats(
          data.vto_bulk_job_items || [],
          toStatusItem
        )
        return {
          ...data,
          processed_items: stats.processed,
          failed_items: stats.failed,
          vto_bulk_job_items: undefined,
        } as unknown as VtoBulkJob
      },
      staleTime: 30 * 1000,
    }),

  findManyByWorkspace: (workspaceId: string) =>
    queryOptions({
      queryKey: ['vto-bulk-jobs', 'workspace', workspaceId],
      queryFn: async ({ signal }): Promise<VtoBulkJob[]> => {
        if (!workspaceId || workspaceId.trim() === '') {
          throw new Error(
            'Invalid workspaceId: cannot fetch VTO bulk jobs without valid workspace ID'
          )
        }

        const { data, error } = await supabase
          .from('vto_bulk_jobs')
          .select(
            `
            *,
            profiles!author(id, email, full_name, avatar_url),
            vto_bulk_job_items!bulk_job_id(
              error_message,
              vto_look_id,
              vto_looks!vto_look_id(
                generations!current_generation_id(status)
              )
            )
          `
          )
          .eq('workspace_id', workspaceId)
          .order('created_at', { ascending: false })
          .abortSignal(signal)

        if (error) {
          throw new Error(`Failed to fetch VTO bulk jobs: ${error.message}`, {
            cause: error,
          })
        }

        return (data || []).map((job) => {
          const stats = countItemStats(
            job.vto_bulk_job_items || [],
            toStatusItem
          )
          return {
            ...job,
            processed_items: stats.processed,
            failed_items: stats.failed,
            vto_bulk_job_items: undefined,
          }
        }) as unknown as VtoBulkJob[]
      },
      staleTime: 30 * 1000,
    }),

  /**
   * Get active bulk jobs with progress info for the indicator.
   * A job is "active" if any item's generation is still pending/processing.
   * Status is derived from items, not the stored status column.
   */
  findManyActiveWithProgress: (workspaceId: string) =>
    queryOptions({
      queryKey: ['vto-bulk-jobs', 'workspace', workspaceId, 'active'],
      queryFn: async ({ signal }): Promise<VtoBulkJobWithProgress[]> => {
        if (!workspaceId || workspaceId.trim() === '') {
          return []
        }

        const { data: jobs, error: jobsError } = await supabase
          .from('vto_bulk_jobs')
          .select(
            `
            id,
            name,
            total_items,
            created_at,
            vto_bulk_job_items!bulk_job_id(
              id,
              vto_look_id,
              error_message,
              vto_looks!vto_look_id(
                current_generation_id,
                generations!current_generation_id(status)
              )
            )
          `
          )
          .eq('workspace_id', workspaceId)
          .order('created_at', { ascending: false })
          .limit(20)
          .abortSignal(signal)

        if (jobsError) {
          throw new Error(
            `Failed to fetch active VTO bulk jobs: ${jobsError.message}`,
            { cause: jobsError }
          )
        }

        if (!jobs || jobs.length === 0) {
          return []
        }

        return jobs
          .map((job) => {
            const stats = countItemStats(
              job.vto_bulk_job_items || [],
              toStatusItem
            )
            return { job, processedCount: stats.processed }
          })
          .filter(({ job, processedCount }) => processedCount < job.total_items)
          .map(({ job, processedCount }) => ({
            id: job.id,
            name: job.name,
            status: 'processing' as const,
            total_items: job.total_items,
            created_at: job.created_at,
            processed_items: processedCount,
            progress_percent:
              job.total_items > 0
                ? Math.round((processedCount / job.total_items) * 100)
                : 0,
          }))
      },
      staleTime: 5 * 1000,
      refetchInterval: (query) => {
        const data = query.state.data
        return data && data.length > 0 ? 5000 : false
      },
    }),
}
